import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useToast } from '@/hooks/useToast';
import { getApiError } from '@/types/apiError';
import { downloadFromSource } from '@/utils/downloadUtils';

export interface ArchiveDownloadProgress {
  loaded: number;
  total?: number;
}

export interface DownloadArchiveParameters {
  folderIds?: string[];
  assetIds?: string[];
  archiveName?: string;
  onProgress?: (progress: ArchiveDownloadProgress) => void;
  signal?: AbortSignal;
}

export const useDownloadArchive = () => {
  const { showToast } = useToast();
  const { getTenantId } = useTenants();

  const mutation = useMutation({
    mutationFn: async (parameters: DownloadArchiveParameters) => {
      showToast({ message: 'Preparing archive...', type: 'info' });
      const response = await api.archiveDownloads.downloadArchive(
        getTenantId(),
        { folderIds: parameters.folderIds, assetIds: parameters.assetIds, archiveName: parameters.archiveName },
        {
          format: 'blob',
          signal: parameters.signal,
          onDownloadProgress: event => {
            parameters.onProgress?.({
              loaded: event.loaded,
              total: event.total ?? readTotalBytesHeader(event.event),
            });
          },
        }
      );
      return response;
    },
    onSuccess: async ({ data, headers }) => {
      await downloadFromSource(
        { data: data as BlobPart, headers: headers as Record<string, string | undefined> },
        { autoDetectFilename: true, fallbackFilename: 'archive.zip' }
      );
      showToast({ message: 'Archive downloaded', type: 'success' });
    },
    onError: async error => {
      if (isCancellation(error)) {
        // User aborted; keep the surface quiet.
        return;
      }
      const message = await extractErrorMessage(error);
      showToast({ message, type: 'error' });
    },
  });

  return {
    downloadArchive: mutation.mutate,
    downloadArchiveAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};

function isCancellation(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  const code = (error as { code?: string } | null | undefined)?.code;
  const name = (error as { name?: string } | null | undefined)?.name;
  return code === 'ERR_CANCELED' || name === 'AbortError' || name === 'CanceledError';
}

/**
 * axios' `onDownloadProgress` event exposes the underlying XHR / fetch event,
 * which carries response headers. If the server's Content-Length is
 * missing, fall back to the custom `X-Archive-Total-Bytes` header the archive endpoint emits.
 */
function readTotalBytesHeader(progressEvent: unknown): number | undefined {
  const target = (progressEvent as { target?: unknown })?.target;
  if (target && typeof (target as { getResponseHeader?: unknown }).getResponseHeader === 'function') {
    const raw = (target as { getResponseHeader: (name: string) => string | null }).getResponseHeader(
      'X-Archive-Total-Bytes'
    );
    if (raw) {
      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return undefined;
}

/**
 * Extract error from Blob response types.
 */
async function extractErrorMessage(error: unknown): Promise<string> {
  const fallback = 'Error downloading archive';
  const response = (error as { response?: { data?: unknown } } | undefined)?.response;
  const data = response?.data;
  if (data instanceof Blob) {
    try {
      const text = await readBlobAsText(data);
      const parsed = JSON.parse(text) as { message?: unknown };
      if (typeof parsed.message === 'string') {
        return parsed.message;
      }
    } catch {
      return fallback;
    }
    return fallback;
  }
  return getApiError(error as Parameters<typeof getApiError>[0], fallback)?.message ?? fallback;
}

function readBlobAsText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  // Fallback for runtimes whose Blob lacks .text() (e.g. jsdom in unit tests).
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(typeof reader.result === 'string' ? reader.result : ''));
    reader.addEventListener('error', () => reject(reader.error ?? new Error('FileReader failed')));
    // eslint-disable-next-line unicorn/prefer-blob-reading-methods
    reader.readAsText(blob);
  });
}
